import React, { useCallback, useState } from 'react';
import { Artist, getArtists } from '@round/api';
import ArtistsAdminTable from './ArtistsAdminTable/ArtistsAdminTable';
import styles from './ArtistsAdmin.module.css';
import CreateArtistModal from '../CreateArtistModal/CreateArtistModal';
import UpdateArtistModal from '../UpdateArtistModal/UpdateArtistModal';
import useUrlState from '../../../../Hooks/useUrlState';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import SearchInput from '../../../../ui/DataEntry/SearchInput/SearchInput';
import Button from '../../../../ui/Buttons/Button/Button';

type UrlState = {
    page: number;
    page_size: number;
    search: string;
};

const initialUrlState: UrlState = {
    page: 1,
    page_size: 25,
    search: '',
};

const ArtistsAdmin = () => {
    const [isCreateArtistModalOpen, setIsCreateArtistModalOpen] = useState(false);
    const [isUpdateArtistModalOpen, setIsUpdateArtistModalOpen] = useState(false);
    const [artistToUpdate, setArtistToUpdate] = useState<Artist | null>(null);

    const [artists, setArtists] = useState<Artist[]>([]);
    const [artistsCount, setArtistsCount] = useState(0);
    const [tableLoading, setTableLoading] = useState(false);
    const [errorLoadingTable, setErrorLoadingTable] = useState(false);

    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState);

    const page = !isNaN(Number(urlState.page)) ? Number(urlState.page) : initialUrlState.page;
    const setPage = (page: number) => setUrlState({ page });
    const pageSize = !isNaN(Number(urlState.page_size)) ? Number(urlState.page_size) : initialUrlState.page_size;
    const setPageSize = (size: number | undefined) => setUrlState({ page_size: size ?? initialUrlState.page_size });
    const search = urlState.search;
    const setSearch = (search: string) => setUrlState({ search, page: initialUrlState.page });

    const initTable = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                setTableLoading(true);
                setErrorLoadingTable(false);
                const artistsResponse = await getArtists({ page, page_size: pageSize, search }, requestInit);
                if (artistsResponse.status === 200) {
                    setArtists(artistsResponse.data.results);
                    setArtistsCount(artistsResponse.data.count);
                }
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setErrorLoadingTable(true);
            } finally {
                setTableLoading(false);
            }
        },
        [page, pageSize, search]
    );

    const refreshAfterUpdate = useCallback(() => {
        setIsUpdateArtistModalOpen(false);
        setArtistToUpdate(null);
        initTable();
    }, [initTable]);

    useAbortableEffect(
        (signal) => {
            initTable({ signal });
        },
        [initTable]
    );

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Artists</h1>

            <div className={styles.toolbar}>
                <SearchInput value={search} onChange={setSearch} className={styles.search} />
                <Button type="filled" color="black" onClick={() => setIsCreateArtistModalOpen(true)}>
                    Create Artist
                </Button>
            </div>

            <ArtistsAdminTable
                disableSortBy
                manualPagination
                noDataLabel={errorLoadingTable ? "Sorry, we're experiencing technical issues" : 'No data available'}
                loading={tableLoading}
                data={artists}
                count={artistsCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                onRowClick={(row) => {
                    setArtistToUpdate(row);
                    setIsUpdateArtistModalOpen(true);
                }}
            />

            <CreateArtistModal
                isOpen={isCreateArtistModalOpen}
                onClose={() => setIsCreateArtistModalOpen(false)}
                onCreated={() => {
                    setIsCreateArtistModalOpen(false);
                    initTable();
                }}
            />

            <UpdateArtistModal
                isOpen={isUpdateArtistModalOpen}
                onClose={() => {
                    setIsUpdateArtistModalOpen(false);
                    setArtistToUpdate(null);
                }}
                onSubmit={refreshAfterUpdate}
                onRefreshed={refreshAfterUpdate}
                artist={artistToUpdate}
            />
        </div>
    );
};

export default ArtistsAdmin;
